import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getStatusDisplay, formatDob, formatDateTime } from '../../../../utils/idCheckStatus';

const BASE_URL = process.env.REACT_APP_API_ENDPOINT || '';

const DetailItem = ({ label, value, alwaysShow = false }) => {
  const isEmpty = value == null || value === '';
  if (isEmpty && !alwaysShow) return null;

  return (
    <div className="id-check-compact__item">
      <span className="id-check-compact__label">{label}</span>
      <span className="id-check-compact__value">{isEmpty ? '—' : value}</span>
    </div>
  );
};

function hubPassLabel(isPass) {
  if (isPass === true) return 'Yes';
  if (isPass === false) return 'No';
  return null;
}

const IdCheckStatusPanel = ({ idCheckSummary }) => {
  const [expanded, setExpanded] = useState(false);

  if (!idCheckSummary?.required && !idCheckSummary?.productsRequireIdCheck) return null;

  const display = getStatusDisplay(idCheckSummary);
  const theme = display.theme || 'neutral';
  const {
    required,
    hub,
    customerDob,
    photoIdRequestSent,
    photoIdDocument,
    idCheckProducts = [],
  } = idCheckSummary;

  const idCheckProductNames = idCheckProducts.map((product) => product.title).join(', ');
  const requiredValue = required
    ? 'Yes'
    : idCheckSummary.isVerified
      ? 'No — customer previously verified'
      : 'No';

  const photoChaseValue = photoIdRequestSent
    ? 'Yes'
    : hub?.isPass === false
      ? 'No'
      : null;

  const hubSummary = hub
    ? [
        hub.resultText ||
          (hub.isPass === true ? 'Pass' : hub.isPass === false ? 'Fail' : null),
        hub.smartScore != null ? `Score ${hub.smartScore}` : null,
        hub.reference ? `Ref ${hub.reference}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || '—'
    : 'Not checked';

  const manualSummary =
    hub?.manualVerifiedBy || hub?.manualVerifiedAt
      ? [hub.manualVerifiedBy, formatDateTime(hub.manualVerifiedAt)].filter(Boolean).join(' · ')
      : null;

  const photoUploadMeta =
    photoIdDocument?.uploadedBy || photoIdDocument?.uploadedAt
      ? [photoIdDocument.uploadedBy, formatDateTime(photoIdDocument.uploadedAt)]
          .filter(Boolean)
          .join(' · ')
      : null;

  const photoUrl = photoIdDocument?.url ? `${BASE_URL}${photoIdDocument.url}` : null;
  const isPdf = photoUrl?.toLowerCase().endsWith('.pdf');

  return (
    <div className="col-12">
      <div className={`order-info-card id-check-compact id-check-compact--${theme}`}>
        <div className="order-info-card__head">
          <h6 className="order-info-card__title">
            <i className="fas fa-id-card" aria-hidden="true" />
            ID Verification
          </h6>
          <span className={`id-check-compact__badge id-check-compact__badge--${theme}`}>
            <i className={`fas ${display.icon}`} aria-hidden="true" />
            {display.label}
          </span>
        </div>

        <div className="order-info-card__body">
          <div className="id-check-compact__body">
            <div className="id-check-compact__details">
              <DetailItem label="ID check products" value={idCheckProductNames} />
              <DetailItem label="DOB" value={formatDob(customerDob)} alwaysShow />
              <DetailItem label="Hub" value={hubSummary} alwaysShow />
              <DetailItem
                label="Hub checked"
                value={formatDateTime(hub?.verifiedAt)}
                alwaysShow
              />
              <DetailItem label="Photo chase" value={photoChaseValue} alwaysShow />
              <DetailItem label="Manual verify" value={manualSummary} alwaysShow />
            </div>

            {photoUrl && (
              <div className="id-check-compact__photo">
                {isPdf ? (
                  <a
                    href={photoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="id-check-compact__photo-link"
                    title={photoIdDocument?.filename || 'View photo ID'}
                  >
                    <i className="fas fa-file-pdf" />
                    <span>PDF</span>
                  </a>
                ) : (
                  <a
                    href={photoUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={photoIdDocument?.filename || 'View photo ID'}
                  >
                    <img src={photoUrl} alt="Photo ID" className="id-check-compact__photo-img" />
                  </a>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="id-check-compact__toggle"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} aria-hidden="true" />
            <span>{expanded ? 'Hide details' : 'Show all details'}</span>
          </button>

          <div
            className={`id-check-compact__collapse${expanded ? ' id-check-compact__collapse--open' : ''}`}
          >
            <div className="id-check-compact__details id-check-compact__details--extra">
              <DetailItem label="Required" value={requiredValue} alwaysShow />
              <DetailItem label="Manual by" value={hub?.manualVerifiedBy} alwaysShow />
              <DetailItem
                label="Manual at"
                value={formatDateTime(hub?.manualVerifiedAt)}
                alwaysShow
              />
              <DetailItem label="Hub result" value={hub?.resultText} alwaysShow />
              <DetailItem
                label="Hub pass"
                value={hubPassLabel(hub?.isPass)}
                alwaysShow
              />
              <DetailItem
                label="Smart score"
                value={hub?.smartScore != null ? String(hub.smartScore) : null}
                alwaysShow
              />
              <DetailItem label="Reference" value={hub?.reference} alwaysShow />
              <DetailItem
                label="Photo ID"
                value={
                  photoIdDocument?.filename ||
                  (photoUrl ? (isPdf ? 'PDF on file' : 'Image on file') : null)
                }
                alwaysShow
              />
              <DetailItem label="Photo uploaded" value={photoUploadMeta} alwaysShow />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

IdCheckStatusPanel.propTypes = {
  idCheckSummary: PropTypes.shape({
    required: PropTypes.bool,
    status: PropTypes.string,
    productsRequireIdCheck: PropTypes.bool,
    idCheckProducts: PropTypes.arrayOf(
      PropTypes.shape({ sku: PropTypes.string, title: PropTypes.string }),
    ),
    customerDob: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isVerified: PropTypes.bool,
    photoIdRequestSent: PropTypes.bool,
    photoIdDocument: PropTypes.object,
    hub: PropTypes.object,
  }),
};

export default IdCheckStatusPanel;
